import { FC, useState } from "react"
import { Box, Button } from "@mui/material"
import { Add } from "@mui/icons-material"
import { enqueueSnackbar } from "notistack"
import { getApiErrorMessage } from "@/core/utils"
import { ConfirmDialog } from "@/modules/userbot/components/common/confirmDialog"
import { ShiftTypeModal, ShiftTypesTable } from "../components/shiftTypes"
import { useScheduleWriteAccess } from "../hooks"
import { StaffSchedule } from "../interfaces"
import { useDeleteShiftTypeMutation, useShiftTypesQuery } from "../store"
import { StaffSchedulePage } from "./staffSchedulePage"

/**
 * Справочник типов смен. Удаление мягкое: тип исчезает из справочника, но уже проставленные
 * смены остаются и рисуются серыми. Повторное удаление уже удалённого типа возвращает 500
 * (контракт, раздел 4.1) — это штатная ошибка мутации, справочник после неё перезапрашивается
 * по тегу ShiftTypes.
 */
export const ShiftTypesPage: FC = () => {
  const { data, isLoading, error } = useShiftTypesQuery()
  const canWrite = useScheduleWriteAccess()
  const [deleteShiftType, { isLoading: isDeleting }] =
    useDeleteShiftTypeMutation()

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<StaffSchedule.ShiftType | null>(null)
  const [deleting, setDeleting] = useState<StaffSchedule.ShiftType | null>(null)

  const handleConfirmDelete = async () => {
    if (!deleting) return
    try {
      await deleteShiftType(deleting.id).unwrap()
      enqueueSnackbar("Тип смены удалён", { variant: "success" })
      setDeleting(null)
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
    }
  }

  return (
    <StaffSchedulePage title="Типы смен">
      {canWrite && (
        <Box mb={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditing(null)
              setModalOpen(true)
            }}
          >
            Добавить тип смены
          </Button>
        </Box>
      )}
      <ShiftTypesTable
        shiftTypes={data}
        isLoading={isLoading}
        error={error}
        canWrite={canWrite}
        onEdit={(t) => {
          setEditing(t)
          setModalOpen(true)
        }}
        onDelete={setDeleting}
      />
      <ShiftTypeModal
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
      />
      <ConfirmDialog
        open={deleting !== null}
        title={`Удалить тип смены «${deleting?.name}»?`}
        description="Тип исчезнет из справочника и его нельзя будет назначать, но уже проставленные смены сохранятся и продолжат отображаться."
        confirmText="Удалить"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </StaffSchedulePage>
  )
}
